import './App.scss';
import React, { useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import MapView from './components/map/MapView';

import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store, AppDispatch, RootState } from './store';
import MqttProvider from './services/mqtt/MqttProvider';

import {
    createBrowserRouter,
    RouterProvider,
    Navigate,
} from 'react-router-dom';
import './index.css';
import EventLayout from './components/views/EventLayout';
import DashboardLayout from './components/views/DashboardLayout';
import OrbatView from './components/orbat/OrbatView';
import SettingsView from './components/views/SettingsView';
import AlarmsView from './components/views/AlarmsView';
import ClientsView from './components/views/ClientsView';
import MapBundlesPage from './components/mapBundles/MapBundlesPage';
import LoginPage from './pages/LoginPage';
import SetPasswordPage from './pages/SetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import AccountsPage from './pages/AccountsPage';
import SettingsPage from './pages/SettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { initializeAuth } from './store/authSlice';
import authService from './services/authService';
import eventService from './services/eventService';

// Auth initializer component that runs on app start
const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { isLoading, isAuthenticated } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        // Only try to initialize auth if we have stored tokens
        if (authService.hasStoredTokens() && !isAuthenticated) {
            dispatch(initializeAuth());
        }
    }, [dispatch, isAuthenticated]);

    // Show loading state only during initial auth check when tokens exist
    if (isLoading && authService.hasStoredTokens() && !isAuthenticated) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};

// Protected routes wrapper component for Dashboard (no MQTT needed)
const ProtectedDashboardLayout: React.FC = () => {
    return (
        <ProtectedRoute>
            <DashboardLayout />
        </ProtectedRoute>
    );
};

// Protected routes wrapper component for Event pages (with MQTT)
const ProtectedEventLayout: React.FC = () => {
    return (
        <ProtectedRoute>
            <MqttProvider>
                <EventLayout />
            </MqttProvider>
        </ProtectedRoute>
    );
};

// Redirect handler for restoring last active event on page reload
const SessionRestoreRedirect: React.FC = () => {
    const lastEventId = eventService.getLastEventId();

    // If user has an active session with a last event, redirect there
    if (lastEventId) {
        return <Navigate to={`/events/${lastEventId}/map`} replace />;
    }

    // Otherwise, go to dashboard
    return <Navigate to="/dashboard" replace />;
};

const router = createBrowserRouter([
    {
        path: '/login',
        element: <LoginPage />,
    },
    {
        path: '/set-password',
        element: <SetPasswordPage />,
    },
    {
        path: '/',
        element: (
            <ProtectedRoute>
                <SessionRestoreRedirect />
            </ProtectedRoute>
        ),
    },
    {
        path: '/dashboard',
        element: <ProtectedDashboardLayout />,
        children: [
            {
                index: true,
                element: <DashboardPage />,
            },
            {
                path: 'users',
                element: <UsersPage />,
            },
            {
                path: 'accounts',
                element: <AccountsPage />,
            },
            {
                path: 'settings',
                element: <SettingsPage />,
            },
        ],
    },
    {
        path: '/events/:eventId',
        element: <ProtectedEventLayout />,
        children: [
            {
                index: true,
                element: <Navigate to="map" replace />,
            },
            {
                path: 'map',
                element: <MapView />,
            },
            {
                path: 'orbat',
                element: <OrbatView />,
            },
            {
                path: 'settings',
                element: <SettingsView />,
            },
            {
                path: 'alarms',
                element: <AlarmsView />,
            },
            {
                path: 'clients',
                element: <ClientsView />,
            },
            {
                path: 'map-bundles',
                element: <MapBundlesPage />,
            },
        ],
    },
    {
        path: '*',
        element: <NotFoundPage />,
    },
]);

library.add(fas);

function App() {
    return (
        <Provider store={store}>
            <AuthInitializer>
                <RouterProvider router={router} />
            </AuthInitializer>
        </Provider>
    );
}

export default App;
